import { useEffect, useCallback } from "react";

// Fullscreen viewer for gallery media. `items` is the flat post list;
// `index` is the active item (or null when closed).
export default function Lightbox({ items, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + items.length) % items.length;
      onNavigate(next);
    },
    [open, index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, go, onClose]);

  if (!open) return null;
  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/95 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center text-cream/70 transition-colors hover:text-gold"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" stroke="currentColor" strokeWidth="1.4">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <NavButton side="left" onClick={(e) => { e.stopPropagation(); go(-1); }} />
      <NavButton side="right" onClick={(e) => { e.stopPropagation(); go(1); }} />

      <div
        className="max-h-[85vh] max-w-[90vw] md:max-w-[70vw]"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === "video" ? (
          <video
            src={item.src}
            poster={item.poster}
            controls
            autoPlay
            loop
            playsInline
            className="max-h-[85vh] w-auto"
          />
        ) : (
          <img
            src={item.src}
            alt=""
            className="max-h-[85vh] w-auto object-contain"
          />
        )}
      </div>
    </div>
  );
}

function NavButton({ side, onClick }) {
  return (
    <button
      aria-label={side === "left" ? "Previous" : "Next"}
      onClick={onClick}
      className={`absolute top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center text-cream/70 transition-colors hover:text-gold ${
        side === "left" ? "left-3 md:left-8" : "right-3 md:right-8"
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-8 w-8" stroke="currentColor" strokeWidth="1.2" fill="none">
        {side === "left" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
      </svg>
    </button>
  );
}
